import {
  ASSOCIATION_KIND_AGENT_LABEL,
  ASSOCIATION_KIND_PULL_REQUEST,
  agentDisplayAssociations,
  getGitRepoAttachment,
} from '@reflex/shared';
import { GitBranch, UserCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { AgentRepoChip } from '@reflex/ui/components/agent/AgentRepoChip';
import { AssociationChip } from '@reflex/ui/components/shared/AssociationChip';
import { cn } from '@reflex/ui/lib/utils';
import { normalizeRepoSlug } from '@reflex/ui/lib/repo-slug';
import { useAgentPersona } from '@reflex/plugin-agent-persona/web/useAgentPersonas';
import { AgentAssociationChips } from '@/components/agent/AgentAssociationChips';
import { AgentRepoBlueprintWarning } from '@/components/agent/AgentRepoBlueprintWarning';
import { AgentHeaderLabelsOverflow } from './AgentHeaderLabelsOverflow';
import { useGitHubWebBaseUrl } from '@/hooks/config/useConfig';
import type { Agent } from '@/lib/types';
import type { ResolvedAgentHeaderBadge } from '@/lib/plugin-registry';

interface AgentHeaderIdentityChipsProps {
  agent: Agent;
  agentHeaderBadges: ResolvedAgentHeaderBadge[];
  hideActions?: boolean;
  /**
   * Plugin / host controls that sit immediately left of the tags trigger.
   */
  beforeLabels?: ReactNode;
  /**
   * Wrap chips onto multiple lines. Desktop header keeps the single-line
   * overflow-hidden row.
   */
  wrap?: boolean;
  className?: string;
}

/**
 * Repo / branch / PR / plugin badges / associations / servers / labels that
 * sit beside the run title on desktop. Hidden below `md`; identity lives in
 * the mobile title-and-tags sheet.
 */
export function AgentHeaderIdentityChips({
  agent,
  agentHeaderBadges,
  hideActions,
  beforeLabels,
  wrap,
  className,
}: AgentHeaderIdentityChipsProps) {
  const githubWebBaseUrl = useGitHubWebBaseUrl();
  // Display-only lookup; see `useAgentPersona`'s docblock for why retry and
  // refetch-on-focus are disabled here.
  const { data: sourcePersona } = useAgentPersona(agent.personaId ?? '', {
    retry: false,
    refetchOnWindowFocus: false,
  });
  const associations = agentDisplayAssociations(agent);
  const labels = associations.filter((a) => a.kind === ASSOCIATION_KIND_AGENT_LABEL);
  const importantAssociations = associations.filter((a) => a.kind !== ASSOCIATION_KIND_AGENT_LABEL);
  const prAssociations = importantAssociations.filter(
    (a) => a.kind === ASSOCIATION_KIND_PULL_REQUEST,
  );
  const otherAssociations = importantAssociations.filter(
    (a) => a.kind !== ASSOCIATION_KIND_PULL_REQUEST,
  );
  const gitRepo = getGitRepoAttachment(agent);
  const repoSlug = gitRepo?.repoSlug?.trim();
  const githubOrigin = githubWebBaseUrl.replace(/\/+$/, '');
  const repoUrl = repoSlug ? `${githubOrigin}/${normalizeRepoSlug(repoSlug)}` : null;
  const repoBranch = gitRepo?.repoBranch?.trim();
  const branchUrl =
    repoUrl && repoBranch
      ? `${repoUrl}/tree/${repoBranch.split('/').map(encodeURIComponent).join('/')}`
      : null;
  const hasDetails =
    Boolean(sourcePersona) ||
    Boolean(repoSlug) ||
    agentHeaderBadges.length > 0 ||
    importantAssociations.length > 0 ||
    labels.length > 0 ||
    Boolean(beforeLabels) ||
    (agent.devboxId && (agent.daemons?.length ?? 0) > 0);

  if (!hasDetails) return null;

  return (
    <div
      className={cn(
        wrap
          ? 'flex flex-wrap items-center gap-1.5'
          : 'flex shrink-0 items-center gap-1 overflow-hidden',
        className,
      )}
      data-testid="agent-header-identity-chips"
    >
      {sourcePersona ? (
        <AssociationChip
          href={null}
          label={sourcePersona.name}
          ariaLabel={`Persona ${sourcePersona.name}`}
          icon={<UserCircle className="h-2.5 w-2.5" aria-hidden="true" />}
          hoverTitle={sourcePersona.name}
          hoverSubtitle={sourcePersona.description ?? null}
          data-testid="agent-header-source-persona"
        />
      ) : null}
      {repoSlug ? (
        <>
          <AgentRepoChip
            repoSlug={repoSlug}
            href={repoUrl ?? undefined}
            variant="badge"
            data-testid="agent-header-repo"
          />
          {!hideActions ? (
            <AgentRepoBlueprintWarning
              repoSlug={repoSlug}
              repoBranch={repoBranch}
              repoAccess={gitRepo?.repoAccess}
            />
          ) : null}
        </>
      ) : null}
      {repoBranch ? (
        <AssociationChip
          href={branchUrl}
          label={repoBranch}
          ariaLabel={`Open branch ${repoBranch} on GitHub`}
          icon={<GitBranch className="h-2.5 w-2.5" aria-hidden="true" />}
          hoverTitle={repoBranch}
          hoverDetail={repoSlug ? `${repoSlug} · branch` : 'Branch'}
          linkLabel="View branch on GitHub"
          data-testid="agent-header-repo-branch"
        />
      ) : null}
      {prAssociations.length > 0 ? <AgentAssociationChips associations={prAssociations} /> : null}
      {agentHeaderBadges.map((badge) => (
        <badge.component key={badge.id} agent={agent} />
      ))}
      {otherAssociations.length > 0 ? (
        <AgentAssociationChips associations={otherAssociations} />
      ) : null}
      {/* Daemons moved out of the header: the composer's AgentActivityDock is
          now the stream view's home for the servers an agent hosts. */}
      {beforeLabels}
      <AgentHeaderLabelsOverflow labels={labels} agentId={hideActions ? undefined : agent.id} />
    </div>
  );
}
